import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { updateAnno } from '../../lib/api';
import AvvisiPanel from '../nav/AvvisiPanel';

/**
 * ThemeHeader — sezione "Tema dell'anno": display + form di edit.
 *
 * Props:
 *   - anno      : { id, anno, tema, perche }
 *   - onSaved() : callback dopo save success (per ricaricare dashboard)
 *
 * Mantiene i `data-testid` originali per non rompere i test e2e.
 */
export default function ThemeHeader({ anno, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [tema, setTema] = useState(anno?.tema || '');
  const [perche, setPerche] = useState(anno?.perche || '');

  const startEdit = () => {
    setTema(anno?.tema || '');
    setPerche(anno?.perche || '');
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setTema(anno?.tema || '');
    setPerche(anno?.perche || '');
  };

  const save = async () => {
    try {
      await updateAnno(anno?.id, { tema, perche });
      setEditing(false);
      toast.success('Tema dell\'anno aggiornato');
      onSaved?.();
    } catch (e) {
      toast.error('Errore salvataggio');
    }
  };

  return (
    <header className="border-b border-line pb-10 md:pb-16 mb-12" data-testid="dashboard-header">
      <AvvisiPanel variant="dashboard" />
      <div className="flex items-start justify-between gap-6 mb-4">
        <span className="mono-label">Tema dell&apos;anno · {anno?.anno}</span>
        {!editing && (
          <button
            onClick={startEdit}
            data-testid="edit-header-btn"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-soft hover:text-ink transition-colors"
          >
            <Pencil size={12} strokeWidth={1.5} /> Modifica
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          <Input
            value={tema}
            onChange={(e) => setTema(e.target.value)}
            placeholder="Una frase. Senza slogan."
            className="rounded-none border-0 border-b border-line focus-visible:ring-0 focus-visible:border-ink text-3xl md:text-5xl font-display font-light px-0 py-2 h-auto bg-transparent"
            data-testid="input-tema"
          />
          <Textarea
            value={perche}
            onChange={(e) => setPerche(e.target.value)}
            placeholder="Perché. Cosa cambia davvero quest'anno."
            className="rounded-none border-0 border-b border-line focus-visible:ring-0 focus-visible:border-ink text-lg md:text-xl font-light leading-relaxed px-0 py-2 bg-transparent resize-none"
            rows={4}
            data-testid="input-perche"
          />
          <div className="flex gap-3">
            <Button
              onClick={save}
              className="rounded-none bg-ink hover:bg-accent text-canvas font-mono text-xs uppercase tracking-[0.2em] h-10 px-6"
              data-testid="save-header-btn"
            >
              Salva
            </Button>
            <button
              onClick={cancelEdit}
              className="font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink"
              data-testid="cancel-header-btn"
            >
              Annulla
            </button>
          </div>
        </div>
      ) : (
        <>
          <h1
            className="font-display font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6"
            data-testid="anno-tema"
          >
            {anno?.tema || (
              <span className="text-mute italic">Definisci il tema dell&apos;anno.</span>
            )}
          </h1>
          {anno?.perche && (
            <p
              className="font-body font-light text-lg md:text-xl lg:text-2xl leading-relaxed max-w-4xl text-ink"
              data-testid="anno-perche"
            >
              {anno.perche}
            </p>
          )}
        </>
      )}
    </header>
  );
}
